import { urlPath } from '../utils/utils';
import Service, { minutes } from './Service';
import type TokenizedApi from '../TokenizedApi';
import type { TLockbox } from '../TokenizedApi';
import { MFARequest } from './MFA';
import * as schema from '../utils/schema';
import {
  restApiProfile,
  restApiEntity,
  TProfile,
} from './TokenizedApiModelEntities';
import { CURRENCY_DEFAULT, DISPLAY_CURRENCIES } from '../utils/instruments';

/** @category SDK Modules */
export default class Entities extends Service {
  /** @internal */
  constructor(tokenizedApi: TokenizedApi) {
    super(tokenizedApi);
  }

  profilesQuery = this.makeQuery(
    {
      // Profiles hardly ever change: Wait 30 minutes before re-query
      staleTime: minutes(30),
    },
    async () => {
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles'),
      );
      const normalized = schema.normalizeResponse(
        'Query profiles',
        response?.data?.data,
        schema.arrayOf(restApiProfile),
      );

      let currencyForLocale = this.tokenizedApi.intl.getLocaleCurrencyCode();
      if (!DISPLAY_CURRENCIES.includes(currencyForLocale)) {
        currencyForLocale = CURRENCY_DEFAULT;
      }

      const allProfiles: TProfile[] = (normalized.normalizedValue || []).map(
        (profile) => ({
          ...profile,
          displayCurrency: profile?.displayCurrency || currencyForLocale,
        }),
      );

      // TODO: get user handle and search for matching personal profile
      const personalProfile = allProfiles.length ? allProfiles[0] : undefined;

      return {
        personalProfile,
        allProfiles,
        idMap: new Map(allProfiles.map((profile) => [profile.id, profile])),
      };
    },
  );

  async updateProfileDetails(
    profileId: string,
    { displayCurrency }: { displayCurrency?: string },
  ) {
    const requestData: { display_currency?: string } = {};

    if (!profileId) {
      throw new TypeError(
        'Update profile details: you must specify a profileId',
      );
    }
    if (displayCurrency) {
      if (typeof displayCurrency !== 'string' || displayCurrency.length !== 3) {
        throw new TypeError(
          'Update profile details: displayCurrency must be a 3-character string if specified',
        );
      }
      requestData.display_currency = displayCurrency;
    }

    await this.tokenizedApi.endpoints.put(
      urlPath('profiles', profileId),
      requestData,
    );

    this.profilesQuery.invalidate();
  }

  entityQuery = this.makeQuery(
    (query, profileId?: string, entityId?: string) => ({
      ...query,
      // Entities hardly ever change: Wait 30 minutes before re-query
      staleTime: minutes(30),
      enabled: query.enabled && !!profileId && !!entityId,
    }),
    async (context, profileId?: string, entityId?: string) => {
      if (!profileId || !entityId) {
        return null;
      }
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'entities', entityId),
      );
      return schema.normalizeResponse(
        'Query entity',
        response?.data?.data,
        restApiEntity,
      ).normalizedValue;
    },
  );

  makeActivityEventForUnconfirmedEntity({
    profileId,
    name,
    entityHandle,
    modifyExistingOrganization,
    otherAdministrators,
    requiredSigners,
    isConfirming,
    ownHandle,
    lockboxes,
  }: {
    profileId?: string;
    name: string;
    entityHandle: string;
    modifyExistingOrganization?: {
      profileId: string;
      entityId: string;
      lockboxId: string;
    };
    otherAdministrators?: string[];
    requiredSigners: number;
    isConfirming?: boolean;
    ownHandle?: string;
    lockboxes?: { lockboxes?: TLockbox[] };
  }) {
    const initialActivityDetails = {
      id: '',
      activityEventType: modifyExistingOrganization
        ? 'reorg'
        : 'lock_box_creation_proposal',
      reorgStage: modifyExistingOrganization ? 'lockbox_creation' : undefined,
      stage: isConfirming ? 'confirming' : 'creating',
      lockboxId: modifyExistingOrganization?.lockboxId,
      lockbox: {
        entityHandle: entityHandle,
        entityName: name,
        entityId: modifyExistingOrganization?.entityId,
        replaces: modifyExistingOrganization?.lockboxId
          ? [modifyExistingOrganization.lockboxId]
          : undefined,
        requiredSigners,
        signers: [
          {
            handle: ownHandle,
            isProposer: true,
          },
          ...(otherAdministrators || []).map((handle) => ({
            handle,
          })),
        ],
      },
      fees: {},
    };

    if (modifyExistingOrganization) {
      return this.tokenizedApi.activity.transformReorg(initialActivityDetails, {
        profileId,
        entityId: undefined,
        ownHandle,
        lockboxes,
      });
    }
    return this.tokenizedApi.activity.transformLockboxCreate(
      initialActivityDetails,
      {
        profileId,
        entityId: undefined,
        isPersonalProfile: undefined,
        ownHandle,
        lockboxes,
      },
    );
  }

  async createEntity({
    profileId,
    name,
    entityHandle,
    otherAdministrators,
    requiredSigners,
    onEntityCreation,
    setMfaRequest,
  }: {
    profileId: string;
    name: string;
    entityHandle: string;
    otherAdministrators?: string[];
    requiredSigners: number;
    onEntityCreation?: (entityId: string) => void;
    setMfaRequest?: (mfaRequest: MFARequest | null) => void;
  }) {
    if (!profileId || typeof profileId !== 'string') {
      throw new TypeError('Create entity: you must specify a profileId');
    }
    if (!name || typeof name !== 'string') {
      throw new TypeError('Create entity: you must specify a name');
    }
    if (!entityHandle || typeof entityHandle !== 'string') {
      throw new TypeError('Create entity: you must specify a handle');
    }
    if (otherAdministrators) {
      if (!Array.isArray(otherAdministrators)) {
        throw new TypeError(
          'Create entity: otherAdministrators must be an array if specified',
        );
      }
      if (
        !otherAdministrators.every(
          (adminHandle) => adminHandle && typeof adminHandle === 'string',
        )
      ) {
        throw new TypeError(
          'Create entity: specify administrators using non-empty strings (handles)',
        );
      }
    }
    if (
      typeof requiredSigners !== 'number' ||
      Math.round(requiredSigners) < 1
    ) {
      throw new TypeError(
        'Create entity: the number of signers must be 1 or greater',
      );
    }
    if (
      otherAdministrators &&
      requiredSigners > otherAdministrators.length + 1
    ) {
      throw new TypeError(
        `Create entity: ${requiredSigners} required signers, but only ${
          otherAdministrators.length + 1
        } administrators`,
      );
    }

    const postfix = this.tokenizedApi.config.userHandleDomain
      ? this.tokenizedApi.account.getUserHandlePostfix()
      : '';

    let clientId;
    if (setMfaRequest) {
      clientId = (await this.tokenizedApi.getApplicationIdentifiers())
        ?.clientId;
    }
    const response = await this.tokenizedApi.endpoints.post(
      urlPath('profiles', profileId, 'entities'),
      {
        name,
        handle: entityHandle + postfix,
        invited_handles: otherAdministrators,
        required_signers: Math.round(requiredSigners),
        handshake_client_id: clientId,
      },
    );
    const result = schema.normalizeResponse(
      'Create entity',
      response.data.data,
      schema.object<{
        id: string;
        handle: string;
        handshakeId?: string;
        activityId: string;
      }>({
        id: schema.string(),
        handle: schema.string(),
        handshake_id: schema.string().as('handshakeId'),
        activity_id: schema.string().as('activityId'),
      }),
    ).normalizedValue;

    if (result?.id) {
      onEntityCreation?.(result.id);
    }

    // Handshake (mobile app confirmation) is optional
    if (setMfaRequest && result?.handshakeId && result?.activityId) {
      await this.tokenizedApi.mfa.waitForMfa(
        result.handshakeId,
        setMfaRequest,
        result.activityId,
      );
    }

    return result;
  }

  async modifyOrganization({
    profileId,
    entityId,
    otherAdministrators,
    requiredSigners,
    setMfaRequest,
  }: {
    profileId: string;
    entityId: string;
    otherAdministrators?: string[];
    requiredSigners: number;
    setMfaRequest?: (mfaRequest: MFARequest | null) => void;
  }) {
    if (!profileId || typeof profileId !== 'string') {
      throw new TypeError('Modify organization: you must specify a profileId');
    }
    if (!entityId || typeof entityId !== 'string') {
      throw new TypeError('Modify organization: you must specify an entityId');
    }
    if (otherAdministrators) {
      if (!Array.isArray(otherAdministrators)) {
        throw new TypeError(
          'Modify organization: otherAdministrators must be an array if specified',
        );
      }
      if (
        !otherAdministrators.every(
          (adminHandle) => adminHandle && typeof adminHandle === 'string',
        )
      ) {
        throw new TypeError(
          'Modify organization: specify administrators using non-empty strings (handles)',
        );
      }
    }
    if (
      typeof requiredSigners !== 'number' ||
      Math.round(requiredSigners) < 1
    ) {
      throw new TypeError(
        'Create entity: the number of signers must be 1 or greater',
      );
    }
    if (
      otherAdministrators &&
      requiredSigners > otherAdministrators.length + 1
    ) {
      throw new TypeError(
        `Create entity: ${requiredSigners} required signers, but only ${
          otherAdministrators.length + 1
        } administrators`,
      );
    }

    let clientId;
    if (setMfaRequest) {
      clientId = (await this.tokenizedApi.getApplicationIdentifiers())
        ?.clientId;
    }
    const response = await this.tokenizedApi.endpoints.post(
      urlPath('profiles', profileId, 'entities', entityId, 'reorg'),
      {
        invited_handles: otherAdministrators,
        required_signers: Math.round(requiredSigners),
        handshake_client_id: clientId,
      },
    );
    const result = schema.normalizeResponse(
      'Modify organization',
      response.data.data,
      schema.object<{ handshakeId?: string; activityId: string }>({
        handshake_id: schema.string().as('handshakeId'),
        activity_id: schema.string().as('activityId'),
      }),
    ).normalizedValue;

    // Handshake (mobile app confirmation) is optional
    if (setMfaRequest && result?.handshakeId && result?.activityId) {
      await this.tokenizedApi.mfa.waitForMfa(
        result.handshakeId,
        setMfaRequest,
        result.activityId,
      );
    }

    return result;
  }

  async updateEntityDetails(
    profileId: string,
    entityId: string,
    {
      name,
      location,
      website,
      bio,
    }: {
      name?: string;
      location?: string;
      website?: string;
      bio?: string;
    },
  ) {
    const requestData: {
      name?: string;
      location?: string;
      website?: string;
      bio?: string;
    } = {};

    if (!profileId || typeof profileId !== 'string') {
      throw new TypeError(
        'Update entity details: you must specify a profileId',
      );
    }
    if (!entityId) {
      throw new TypeError(
        'Update entity details: you must specify an entityId',
      );
    }
    if (name) {
      if (typeof name !== 'string') {
        throw new TypeError(
          'Update entity details: name must be a string if specified',
        );
      }
      requestData.name = name;
    }
    if (location) {
      if (typeof location !== 'string') {
        throw new TypeError(
          'Update entity details: location must be a string if specified',
        );
      }
      requestData.location = location;
    }
    if (website) {
      if (typeof website !== 'string') {
        throw new TypeError(
          'Update entity details: website must be a string if specified',
        );
      }
      requestData.website = website;
    }
    if (bio) {
      if (typeof bio !== 'string') {
        throw new TypeError(
          'Update entity details: bio must be a string if specified',
        );
      }
      requestData.bio = bio;
    }

    await this.tokenizedApi.endpoints.put(
      urlPath('profiles', profileId, 'entities', entityId),
      requestData,
    );

    this.entityQuery.invalidate();
    this.tokenizedApi.relationships.handleQuery.invalidate();
    this.tokenizedApi.relationships.relationshipsQuery.invalidate();
  }

  async updateEntityAvatar(
    profileId: string,
    entityId: string,
    imageBlob: Blob,
  ) {
    if (!profileId) {
      throw new TypeError('Update entity avatar: you must specify a profileId');
    }
    if (!entityId) {
      throw new TypeError('Update entity avatar: you must specify an entityId');
    }
    if (!(imageBlob instanceof Blob)) {
      throw new TypeError(
        'Update entity avatar: imageBlob must be a Blob object',
      );
    }

    const formData = new FormData();
    formData.append('avatar', imageBlob);
    await this.tokenizedApi.endpoints.post(
      urlPath('profiles', profileId, 'entities', entityId, 'avatar'),
      formData,
    );

    this.entityQuery.invalidate();
    this.tokenizedApi.relationships.handleQuery.invalidate();
    this.tokenizedApi.relationships.relationshipsQuery.invalidate();
  }

  async removeEntityAvatar(profileId: string, entityId: string) {
    if (!profileId) {
      throw new TypeError('Remove entity avatar: you must specify a profileId');
    }
    if (!entityId) {
      throw new TypeError('Remove entity avatar: you must specify an entityId');
    }

    await this.tokenizedApi.endpoints.delete(
      urlPath('profiles', profileId, 'entities', entityId, 'avatar'),
    );

    this.entityQuery.invalidate();
    this.tokenizedApi.relationships.handleQuery.invalidate();
    this.tokenizedApi.relationships.relationshipsQuery.invalidate();
  }
}
